import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

/**
 * GradientProgressBar
 * 
 * A progress bar with gradient styling.
 * 
 * Props:
 *  - value: Current progress value (0-100)
 *  - variant: Color variant ('purple-green' | 'green' | 'purple' | 'primary')
 *  - size: Bar height ('sm' | 'md' | 'lg')
 *  - animated: Whether to animate the progress transition
 *  - className: Additional CSS classes
 *  - ariaLabel: Accessibility label
 */
export default function GradientProgressBar({
  value = 0,
  variant = 'purple-green',
  size = 'md',
  animated = true,
  className = '',
  ariaLabel = 'Progress',
}) {
  // Clamp value between 0 and 100
  const clampedValue = Math.max(0, Math.min(100, value));

  return (
    <div
      className={classNames(
        'gradient-progress',
        `gradient-progress-${size}`,
        className
      )}
      role="progressbar"
      aria-valuenow={clampedValue}
      aria-valuemin="0"
      aria-valuemax="100"
      aria-label={ariaLabel}
    >
      <div
        className={classNames(
          'gradient-progress-bar',
          `gradient-progress-bar-${variant}`,
          { 'gradient-progress-bar-animated': animated }
        )}
        style={{ width: `${clampedValue}%` }}
      />
    </div>
  );
}

GradientProgressBar.propTypes = {
  value: PropTypes.number,
  variant: PropTypes.oneOf(['purple-green', 'green', 'purple', 'primary']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  animated: PropTypes.bool,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
};
